/**
 * A menu you can operate with a thumbstick and a trigger, or with nothing but
 * your eyes and one finger.
 *
 * The DOM menus are focus-and-click, driven by the keyboard because the UI is
 * never allowed to take the pointer lock (see the header of `ui/ui.ts`). None
 * of that survives into a headset: there is no focus ring to see, no keyboard,
 * and no pointer. So the in-world menus are their own widget with their own
 * selection, and they reach the game through exactly the same `UIAction`s the
 * DOM buttons dispatch — nothing downstream can tell which one was used.
 *
 * Two ways in, and the second one is the one that has to work everywhere:
 *
 *  - **Stick and trigger.** `moveZ` walks the list, `moveX` changes a value,
 *    `interact` presses the row. Read off `InputAPI` and nothing else, so
 *    whatever the controllers end up bound to, this follows.
 *  - **Point and press.** A ray lands somewhere on the panel and that row is
 *    selected *and* acted on in the same frame. This is the only mode an Apple
 *    Vision Pro can offer — its ray exists solely at the instant of a pinch —
 *    so nothing here may require a hover, a cursor or a stick to have happened
 *    first. See `pointer.ts`.
 *
 * One rule covers every row for pointing, and it is the same rule the stick
 * already obeys: **the left half of a row means "less" and the right half means
 * "more"**; a row with no value runs on either half. That is why a value row
 * draws a hairline down its middle. Rows are also generous by construction — a
 * press anywhere inside the panel snaps to the nearest row, because a gaze ray
 * is steadier than a mouse but nothing like as precise, and the player cannot
 * see where theirs is going until after they have committed.
 *
 * Feedback has to survive having no hover, so it happens *after* the press: the
 * pressed row flashes and the bottom line of the panel echoes what it now says.
 * `PressMark` in `panel.ts` does the other half, in the room.
 */

import { clamp } from '../../core/math';
import type { InputAPI } from '../../core/types';
import { CanvasPanel, type QuadHit } from './panel';
import type { PointerStyle } from './pointer';
import { AMBER, AMBER_DIM, AMBER_FAINT, BAD, bar, clear, face, fitPx, LINE, LINE_FAINT, rule, text, wrap } from './theme';

/** Stick deflection that counts as a press. Above any resting drift. */
const AXIS_THRESHOLD = 0.55;
/** Seconds before a held stick starts repeating, and between repeats after. */
const REPEAT_DELAY = 0.42;
const REPEAT_RATE = 0.15;

/** Rows visible at once. Longer lists scroll a window around the selection. */
const WINDOW = 7;

/**
 * Rows of tolerance above and below the list a press may land in and still be
 * snapped onto it. One row is about 2.6° at the dock's distance; a gaze ray is
 * good to rather less than that, and the alternative to snapping is a press
 * that silently does nothing.
 */
const SNAP_ROWS = 1;

/**
 * Seconds the pressed row stays lit, and how many repaints that fade is spent
 * over.
 *
 * Quantised on purpose. Every repaint is a full re-upload of a megapixel canvas
 * plus its mipmaps, which is the one genuinely expensive thing this panel does,
 * so the smooth part of the confirmation is carried by the ring in the room and
 * the panel itself only steps down a few times.
 */
const FLASH_LIFE = 0.5;
const FLASH_STEPS = 4;

/** What a pointer press landed on. */
export interface RowHit {
  index: number;
  /** -1 less, +1 more, 0 for a row that just runs. */
  dir: number;
}

export type MenuRow =
  | {
      kind: 'action';
      label: string;
      run(): void;
      /**
       * Second label for a destructive row. Mirrors `armedButton` in
       * `ui/menus.ts`: RESTART and QUIT end a run, and a stick that slipped
       * should not be able to do that in one press.
       */
      confirm?: string;
    }
  | { kind: 'toggle'; label: string; get(): boolean; set(v: boolean): void }
  | {
      kind: 'range';
      label: string;
      min: number;
      max: number;
      step: number;
      get(): number;
      set(v: number): void;
      format(v: number): string;
    }
  | { kind: 'choice'; label: string; options: readonly string[]; get(): string; set(v: string): void }
  /** Not selectable. A stat line, or the row that says the list is empty. */
  | { kind: 'info'; label: string; value?: string };

export interface MenuModel {
  /**
   * Identity of the *screen*, not of the contents. Selection is kept while the
   * key holds and reset when it changes, so adjusting a slider does not send
   * the cursor back to the top of the settings list.
   */
  key: string;
  title: string;
  sub?: string;
  /** Paragraphs under the subtitle: a death's cause, a note's text. */
  body?: readonly string[];
  rows: readonly MenuRow[];
  hint?: string;
}

/** Edge detection with auto-repeat, off a continuous axis. */
class Repeater {
  private dir = 0;
  private timer = 0;
  /**
   * True until the stick has been seen at rest.
   *
   * A menu very often opens while the player is holding the stick forward —
   * pause is pressed mid-corridor — and without this the pause screen would
   * arrive with the cursor already moved off RESUME.
   */
  private waitingForNeutral = true;

  step(v: number, dt: number): number {
    const dir = v > AXIS_THRESHOLD ? 1 : v < -AXIS_THRESHOLD ? -1 : 0;
    if (dir === 0) {
      this.dir = 0;
      this.waitingForNeutral = false;
      return 0;
    }
    if (this.waitingForNeutral) return 0;
    if (dir !== this.dir) {
      this.dir = dir;
      this.timer = REPEAT_DELAY;
      return dir;
    }
    this.timer -= dt;
    if (this.timer > 0) return 0;
    this.timer = REPEAT_RATE;
    return dir;
  }

  reset(): void {
    this.dir = 0;
    this.timer = 0;
    this.waitingForNeutral = true;
  }
}

/** Where the rows landed on the canvas, in pixels, at the last repaint. */
interface RowLayout {
  /** Top of the first drawn row. */
  top: number;
  height: number;
  /** First and last row index actually drawn. */
  from: number;
  to: number;
  /** Canvas x at which a row stops meaning "less" and starts meaning "more". */
  split: number;
}

export class MenuPanel {
  readonly panel = new CanvasPanel(0.86, 0.7);

  private model: MenuModel | null = null;
  private selected = 0;
  /** Index of the destructive row waiting for its second press, or -1. */
  private armed = -1;
  private scroll = 0;
  private dirty = false;
  private alpha = 0;

  private readonly vertical = new Repeater();
  private readonly horizontal = new Repeater();

  private layout: RowLayout | null = null;
  private style: PointerStyle = 'unknown';

  /** The row a continuous ray is resting on, so hover only moves on a change. */
  private hovered = -1;

  /** The row a held press latched onto, and which way it was leaning. */
  private heldRow = -1;
  private heldDir = 0;
  private repeatIn = 0;

  /** Press feedback: which row, what it now says, and how long it has left. */
  private flashRow = -1;
  private flashEcho = '';
  private flashLeft = 0;

  constructor() {
    this.panel.setOpacity(0);
  }

  /** Hand over this frame's screen, or null to take the menu away. */
  setModel(model: MenuModel | null): void {
    const changed = (this.model?.key ?? null) !== (model?.key ?? null);
    this.model = model;
    if (changed) {
      this.selected = model ? firstSelectable(model.rows) : 0;
      this.armed = -1;
      this.scroll = 0;
      this.hovered = -1;
      this.heldRow = -1;
      this.flashRow = -1;
      this.flashLeft = 0;
      this.vertical.reset();
      this.horizontal.reset();
    }
    this.dirty = true;
  }

  get open(): boolean {
    return this.model !== null;
  }

  /**
   * What the session can do, which decides the words on the bottom line.
   *
   * A headset with no stick must not be told to push one, and this is the only
   * place in the in-world menus that names a control.
   */
  setInputStyle(style: PointerStyle): void {
    if (style === this.style) return;
    this.style = style;
    this.dirty = true;
  }

  /**
   * One frame of controller. Returns true when the player did something, which
   * `layer.ts` uses to rebuild the model — a toggle that was just flipped has
   * to be redrawn showing its new value.
   *
   * `pointerTook` is set when a ray has already spent this frame's press on a
   * row it was actually aimed at. Without it, one pinch would both press the
   * row under the ray and press the row under the cursor, which on a device
   * whose ray only exists during a pinch is every press.
   */
  step(input: InputAPI, dt: number, pointerTook: boolean): boolean {
    const model = this.model;
    if (!model) return false;
    let acted = false;

    const dv = this.vertical.step(input.moveZ, dt);
    if (dv !== 0) {
      // Stick forward is up the list. Moving the cursor disarms whatever was
      // waiting for a confirm, which is what makes arming safe.
      this.move(-dv);
      this.armed = -1;
      acted = true;
    }

    const dh = this.horizontal.step(input.moveX, dt);
    if (dh !== 0 && this.adjust(dh)) acted = true;

    if (!pointerTook && input.pressed('interact') && this.activate()) {
      this.flash(this.selected);
      acted = true;
    }
    if (acted) this.dirty = true;
    return acted;
  }

  update(dt: number): void {
    if (this.flashLeft > 0) {
      const was = flashStep(this.flashLeft);
      this.flashLeft = Math.max(0, this.flashLeft - dt);
      if (flashStep(this.flashLeft) !== was) this.dirty = true;
    }
    if (this.dirty) {
      this.dirty = false;
      this.draw();
    }
    const target = this.model ? 1 : 0;
    this.alpha += Math.sign(target - this.alpha) * Math.min(Math.abs(target - this.alpha), dt / 0.18);
    this.panel.setOpacity(this.alpha);
  }

  dispose(): void {
    this.panel.dispose();
  }

  /* ---------------------------------------------------------------- *
   * Pointing
   * ---------------------------------------------------------------- */

  /**
   * Which row a ray that landed at `hit` is asking for, or null.
   *
   * Generous by design: anywhere inside the list is the nearest row, a press up
   * to `SNAP_ROWS` past either end is pulled back onto it, and a row that
   * cannot be selected hands the press to its nearest neighbour that can. The
   * only misses are presses well above the list — the title and the body text,
   * where there is nothing to hit and pretending otherwise would fire a row the
   * player never looked at.
   */
  hit(at: QuadHit): RowHit | null {
    const rows = this.model?.rows;
    if (!rows || rows.length === 0) return null;
    // The layout is only written when the canvas is painted, and a model handed
    // over this frame has not been painted yet.
    if (this.dirty) {
      this.dirty = false;
      this.draw();
    }
    const layout = this.layout;
    if (!layout || layout.to < layout.from) return null;

    const y = at.v * this.panel.height;
    const offset = Math.floor((y - layout.top) / layout.height);
    const span = layout.to - layout.from;
    if (offset < -SNAP_ROWS || offset > span + SNAP_ROWS) return null;

    const index = nearestSelectable(rows, clamp(layout.from + offset, layout.from, layout.to));
    if (index < 0) return null;
    const row = rows[index]!;
    const dir = row.kind === 'action' ? 0 : at.u * this.panel.width >= layout.split ? 1 : -1;
    return { index, dir };
  }

  /**
   * A continuous ray is resting on a row. Returns true when that moved the
   * selection.
   *
   * Only acts when the row under the ray *changes*, so a controller left lying
   * on the panel does not fight the stick for the cursor. Nothing depends on
   * this happening: a session with no continuous ray simply never calls it.
   */
  hover(row: RowHit | null): boolean {
    const index = row ? row.index : -1;
    if (index === this.hovered) return false;
    this.hovered = index;
    if (index < 0 || index === this.selected) return false;
    this.selected = index;
    this.armed = -1;
    this.scrollTo(index);
    this.dirty = true;
    return true;
  }

  /**
   * Press a row that was pointed at. Returns true when something changed.
   *
   * Selection and action land in the same frame, which is the whole point: on a
   * gaze-and-pinch headset there is no earlier frame in which the player could
   * have told us what they were aiming at.
   */
  press(row: RowHit): boolean {
    const rows = this.model?.rows;
    if (!rows) return false;
    if (row.index !== this.selected) {
      this.selected = row.index;
      // Aiming somewhere else is as good a disarm as moving the cursor there.
      this.armed = -1;
    }
    this.scrollTo(row.index);
    this.hovered = row.index;
    this.heldRow = row.index;
    this.heldDir = row.dir;
    this.repeatIn = REPEAT_DELAY;

    const acted = row.dir === 0 ? this.activate() : this.adjust(row.dir);
    // Flashed whether or not the value moved: a slider already at its maximum
    // still has to answer the press, or the player cannot tell a stuck control
    // from a missed one.
    this.flash(row.index);
    this.dirty = true;
    return acted;
  }

  /**
   * A press that is still held. Returns true when it stepped the row again.
   *
   * Latched onto the row the press started on rather than re-aimed each frame,
   * because a held pinch drifts and a slider that changed rows halfway through
   * would be unusable. This is how a slider is dragged on a device with no
   * stick to hold over.
   */
  holdStep(dt: number): boolean {
    if (this.heldRow < 0 || this.heldDir === 0) return false;
    const row = this.model?.rows[this.heldRow];
    if (!row || row.kind !== 'range') return false;
    this.repeatIn -= dt;
    if (this.repeatIn > 0) return false;
    this.repeatIn = REPEAT_RATE;
    const acted = this.adjust(this.heldDir);
    this.flash(this.heldRow);
    this.dirty = true;
    return acted;
  }

  /** The press came up. */
  release(): void {
    this.heldRow = -1;
    this.heldDir = 0;
  }

  /* ---------------------------------------------------------------- *
   * Driving
   * ---------------------------------------------------------------- */

  private move(delta: number): void {
    const rows = this.model?.rows ?? [];
    if (rows.length === 0) return;
    let i = this.selected;
    for (let n = 0; n < rows.length; n++) {
      i = (i + delta + rows.length) % rows.length;
      if (rows[i]?.kind !== 'info') break;
    }
    this.selected = i;
    this.scrollTo(i);
  }

  /** Keep a row inside the visible window, scrolling by the minimum. */
  private scrollTo(index: number): void {
    const rows = this.model?.rows ?? [];
    this.scroll = clamp(this.scroll, index - WINDOW + 1, index);
    this.scroll = clamp(this.scroll, 0, Math.max(0, rows.length - WINDOW));
  }

  /**
   * Light a row and say what it now reads, for as long as it takes somebody to
   * look down at it.
   *
   * This is the half of the design that hover cannot do. On a controller the
   * highlight under the cursor already says "this is what you are about to
   * press"; on a gaze-and-pinch headset there is no *about to*, so the panel
   * has to answer afterwards instead — which row went, and what it says now.
   */
  private flash(index: number): void {
    const row = this.model?.rows[index];
    if (!row) return;
    this.flashRow = index;
    this.flashLeft = FLASH_LIFE;
    const value = valueText(row);
    // A row that has armed itself says so here as well as in red on the row: a
    // press that appears to have done nothing is the worst thing a menu with no
    // hover can do, and this is the one row where nothing is what should happen.
    this.flashEcho =
      this.armed === index && row.kind === 'action' && row.confirm
        ? `${row.confirm} — PRESS AGAIN`
        : value
          ? `${row.label} — ${value}`
          : row.label;
    this.dirty = true;
  }

  private adjust(dir: number): boolean {
    const row = this.model?.rows[this.selected];
    if (!row) return false;
    switch (row.kind) {
      case 'range': {
        // Snapped to the step so a slider nudged twenty times does not end up
        // at 0.8500000000000003 in the save file.
        const raw = clamp(row.get() + dir * row.step, row.min, row.max);
        const next = clamp(Math.round(raw / row.step) * row.step, row.min, row.max);
        if (Math.abs(next - row.get()) < 1e-9) return false;
        row.set(next);
        return true;
      }
      case 'choice': {
        const i = row.options.indexOf(row.get());
        const next = row.options[clamp(i + dir, 0, row.options.length - 1)];
        if (next === undefined || next === row.get()) return false;
        row.set(next);
        return true;
      }
      case 'toggle': {
        const want = dir > 0;
        if (row.get() === want) return false;
        row.set(want);
        return true;
      }
      default:
        return false;
    }
  }

  private activate(): boolean {
    const row = this.model?.rows[this.selected];
    if (!row) return false;
    switch (row.kind) {
      case 'action':
        if (row.confirm && this.armed !== this.selected) {
          this.armed = this.selected;
          return true;
        }
        this.armed = -1;
        row.run();
        return true;
      case 'toggle':
        row.set(!row.get());
        return true;
      case 'choice': {
        const i = row.options.indexOf(row.get());
        const next = row.options[(i + 1) % row.options.length];
        if (next !== undefined) row.set(next);
        return true;
      }
      default:
        return false;
    }
  }

  /* ---------------------------------------------------------------- *
   * Painting
   * ---------------------------------------------------------------- */

  private draw(): void {
    const model = this.model;
    const p = this.panel;
    if (!model) {
      // The panel is fading out over the room; leave nothing behind it.
      p.paint((c, w, h) => clear(c, w, h));
      return;
    }
    p.paint((c, w, h) => {
      face(c, w, h, LINE);
      const pad = p.px(0.042);
      let y = pad + p.px(0.022);

      text(c, model.title, pad, y, {
        px: p.px(0.03),
        color: AMBER,
        tracking: p.px(0.005),
      });
      y += p.px(0.038);
      rule(c, pad, y, w - pad * 2, LINE);
      y += p.px(0.03);

      if (model.sub) {
        const style = { px: p.px(0.021), color: AMBER_FAINT };
        for (const l of wrap(c, model.sub, w - pad * 2, style)) {
          text(c, l, pad, y, style);
          y += p.px(0.03);
        }
        y += p.px(0.008);
      }

      for (const para of model.body ?? []) {
        const style = { px: p.px(0.023), color: AMBER_DIM };
        for (const l of wrap(c, para, w - pad * 2, style)) {
          text(c, l, pad, y, style);
          y += p.px(0.032);
        }
        y += p.px(0.012);
      }

      const end = Math.min(model.rows.length, this.scroll + WINDOW);
      // Rows grow to fill the panel on hardware that has to aim at them. A row
      // is 2.6° tall at the base height, which is about the error in a gaze ray
      // — so on a short list, where there is room going spare, it is spent on
      // making the targets bigger rather than on white space. A stick does not
      // need this and does not get it: the layout stays exactly as it was.
      const base = p.px(0.056);
      const room = h - p.px(0.062) - y;
      const rowH = this.style === 'point' ? clamp(room / (end - this.scroll), base, base * 1.6) : base;
      // Recorded for the hit test, which has no other way of knowing where the
      // title and the body text pushed the list down to.
      this.layout = { top: y, height: rowH, from: this.scroll, to: end - 1, split: w / 2 };
      for (let i = this.scroll; i < end; i++) {
        this.drawRow(c, p, model.rows[i]!, i, pad, y, w, rowH);
        y += rowH;
      }

      if (model.rows.length > WINDOW) {
        text(c, `${this.selected + 1}/${model.rows.length}`, w - pad, h - pad, {
          px: p.px(0.018),
          color: AMBER_FAINT,
          align: 'right',
        });
      }
      // The echo of the last press takes the bottom line while it lasts. It is
      // the receipt, and it belongs where the player was already told what the
      // controls are.
      const foot = this.flashLeft > 0 ? this.flashEcho : (model.hint ?? this.controlHint());
      const footStyle = {
        px: p.px(0.018),
        color: this.flashLeft > 0 ? AMBER : AMBER_FAINT,
        tracking: p.px(0.0012),
      };
      text(c, foot, pad, h - pad, { ...footStyle, px: fitPx(c, foot, w - pad * 2 - p.px(0.1), footStyle) });
    });
  }

  /** How this session's hardware is told to drive a menu. */
  private controlHint(): string {
    return this.style === 'point' ? 'LOOK AT A ROW AND PINCH · LEFT LOWERS, RIGHT RAISES' : 'STICK MOVE · TRIGGER SELECT';
  }

  private drawRow(
    c: CanvasRenderingContext2D,
    p: CanvasPanel,
    row: MenuRow,
    index: number,
    pad: number,
    y: number,
    w: number,
    rowH: number,
  ): void {
    const on = index === this.selected && row.kind !== 'info';
    const mid = y + rowH / 2 - p.px(0.004);
    const lit = index === this.flashRow ? flashStep(this.flashLeft) / FLASH_STEPS : 0;
    const left = pad - p.px(0.012);
    const width = w - pad * 2 + p.px(0.024);
    if (on || lit > 0) {
      c.globalAlpha = 1;
      c.fillStyle = LINE_FAINT;
      c.fillRect(left, y, width, rowH - p.px(0.008));
    }
    if (lit > 0) {
      // The press, answered. Amber over the selection wash rather than instead
      // of it, so a row that was already selected still visibly changes.
      c.globalAlpha = lit * 0.3;
      c.fillStyle = AMBER;
      c.fillRect(left, y, width, rowH - p.px(0.008));
      c.globalAlpha = 1;
    }
    // The seam between "less" and "more" for a pointed press. Drawn on every
    // row that has a value, and only once there is something in the session
    // that can point: on a controller the stick already does this and the line
    // would be one more mark in a game that is trying not to have a HUD.
    if (this.style === 'point' && row.kind !== 'action' && row.kind !== 'info') {
      c.globalAlpha = 1;
      c.fillStyle = LINE_FAINT;
      c.fillRect(w / 2, y + p.px(0.008), Math.max(1, p.px(0.0012)), rowH - p.px(0.024));
    }

    const armed = this.armed === index && row.kind === 'action' && row.confirm;
    const label = armed && row.kind === 'action' ? row.confirm! : row.label;
    const colour = armed ? BAD : on ? AMBER : row.kind === 'info' ? AMBER_FAINT : AMBER_DIM;
    const labelStyle = { px: p.px(0.026), color: colour };
    const labelX = pad + p.px(0.026);
    if (on) text(c, '>', pad, mid, { px: p.px(0.026), color: AMBER });
    text(c, label, labelX, mid, { ...labelStyle, px: fitPx(c, label, w * 0.56, labelStyle) });

    const valueStyle = { px: p.px(0.024), color: on ? AMBER : AMBER_DIM, align: 'right' as const };
    switch (row.kind) {
      case 'toggle':
        text(c, row.get() ? 'ON' : 'OFF', w - pad, mid, valueStyle);
        break;
      case 'choice':
        text(c, row.get().toUpperCase(), w - pad, mid, valueStyle);
        break;
      case 'range': {
        const value = row.format(row.get());
        const vw = p.px(0.09);
        text(c, value, w - pad, mid, valueStyle);
        const barW = p.px(0.2);
        bar(
          c,
          w - pad - vw - barW,
          mid - p.px(0.005),
          barW,
          p.px(0.009),
          (row.get() - row.min) / (row.max - row.min || 1),
          on ? AMBER : AMBER_DIM,
        );
        break;
      }
      case 'info':
        if (row.value) text(c, row.value, w - pad, mid, { ...valueStyle, color: AMBER_DIM });
        break;
      default:
        break;
    }
  }
}

function firstSelectable(rows: readonly MenuRow[]): number {
  for (let i = 0; i < rows.length; i++) {
    if (rows[i]?.kind !== 'info') return i;
  }
  return 0;
}

/**
 * The selectable row nearest `index`, searching outwards, or -1.
 *
 * A press that lands on a heading — `COMFORT`, or the line that says the pack
 * is empty — is not a mistake to be punished. It is somebody aiming at the row
 * they wanted and being a degree out.
 */
function nearestSelectable(rows: readonly MenuRow[], index: number): number {
  if (rows[index] && rows[index]!.kind !== 'info') return index;
  for (let d = 1; d < rows.length; d++) {
    const below = rows[index + d];
    if (below && below.kind !== 'info') return index + d;
    const above = rows[index - d];
    if (above && above.kind !== 'info') return index - d;
  }
  return -1;
}

/** The flash, in whole repaints. See `FLASH_STEPS`. */
function flashStep(left: number): number {
  return left <= 0 ? 0 : Math.ceil((left / FLASH_LIFE) * FLASH_STEPS);
}

/** What a row currently reads, for the echo under a press. */
function valueText(row: MenuRow): string {
  switch (row.kind) {
    case 'toggle':
      return row.get() ? 'ON' : 'OFF';
    case 'choice':
      return row.get().toUpperCase();
    case 'range':
      return row.format(row.get());
    default:
      return '';
  }
}
